import React, { useCallback } from 'react';
import { Button } from '@vibe/core';
import type { LeaveReportResponse } from '../../api/report.api';
import {
  createWorkbook, addTitleRow, styleHeaderRow, styleGroupRow, styleDataRow,
  setColumnWidths, downloadWorkbook, fillCell, hexToArgb, lightenHex, COLORS,
} from '../../utils/export-report-excel';

interface Props {
  data: LeaveReportResponse;
}

const LeaveReportExportButton: React.FC<Props> = ({ data }) => {
  const handleExport = useCallback(async () => {
    try {
    const { columns, groups, leaveTypes } = data;

    const wb = createWorkbook();
    const ws = wb.addWorksheet('Leave Report');

    addTitleRow(ws, 'Leave Report', data.period.startDate, data.period.endDate);

    // Header row
    const headers = [
      'Team / Person',
      'Total days',
      'H days',
      ...leaveTypes.map((lt) => `${lt.abbreviation} days`),
      ...columns.map((col) => col.label),
    ];
    const headerRow = ws.addRow(headers);
    styleHeaderRow(ws, headerRow);

    // Color leave-type header cells with their colors
    for (let i = 0; i < leaveTypes.length; i++) {
      const cell = headerRow.getCell(4 + i);
      const lightBg = lightenHex(leaveTypes[i].color, 0.6);
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: hexToArgb(lightBg) } };
      cell.font = { bold: true, size: 10, color: { argb: hexToArgb(COLORS.primaryText) } };
    }

    // Column widths
    setColumnWidths(ws, [
      28, 12, 10,
      ...leaveTypes.map(() => 12),
      ...columns.map(() => 16),
    ]);

    for (const group of groups) {
      if (groups.length > 1 || group.groupName !== 'All Users') {
        const groupRow = ws.addRow([group.groupName, ...new Array(headers.length - 1).fill('')]);
        styleGroupRow(groupRow);
      }

      for (const row of group.rows) {
        const excelRow = ws.addRow([
          row.userName,
          row.totalDays,
          row.holidayDays,
          ...leaveTypes.map((lt) => {
            const typeSummary = row.leaveTypeDays.find((td) => td.leaveTypeId === lt.id);
            const days = typeSummary?.days ?? 0;
            return days > 0 ? days : '';
          }),
          ...columns.map((col) => {
            const cell = row.cells.find((c) => c.startDate === col.startDate);
            if (!cell || cell.entries.length === 0) return '';
            const parts = cell.entries.map((e) => `${e.abbreviation} ${e.days}`);
            if (cell.totalHours > 0) {
              parts.push(`${cell.totalHours} H`);
            }
            return parts.join(', ');
          }),
        ]);
        styleDataRow(excelRow);
        excelRow.getCell(1).font = { bold: true, size: 10 };

        // Color leave-type day count cells
        for (let i = 0; i < leaveTypes.length; i++) {
          const val = excelRow.getCell(4 + i).value;
          if (val && Number(val) > 0) {
            fillCell(excelRow.getCell(4 + i), lightenHex(leaveTypes[i].color, 0.7), leaveTypes[i].color, true);
          }
        }

        // Color period cells with leave entries
        const fixedCols = 3 + leaveTypes.length;
        for (let i = 0; i < columns.length; i++) {
          const cell = row.cells.find((c) => c.startDate === columns[i].startDate);
          if (!cell || cell.entries.length === 0) continue;

          const excelCell = excelRow.getCell(fixedCols + 1 + i);
          // Use the first entry's color for the cell background
          const primaryColor = cell.entries[0].color || COLORS.holiday;
          fillCell(excelCell, lightenHex(primaryColor, 0.7), primaryColor, true);
          excelCell.font = { size: 9, bold: true, color: { argb: hexToArgb(primaryColor) } };
        }
      }
    }

    await downloadWorkbook(wb, `leave_report_${data.period.startDate}_${data.period.endDate}.xlsx`);
    } catch (err) {
      console.error('Excel export failed:', err);
    }
  }, [data]);

  return (
    <Button
      kind={Button.kinds.SECONDARY}
      size={Button.sizes.SMALL}
      onClick={handleExport}
    >
      Export Excel
    </Button>
  );
};

export default LeaveReportExportButton;
